<template>
  <section class="all-documents-view !rounded-xl heightClass" v-loading="loading">
    <div
      class="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:!gap-4 !px-3 !pb-2 border-b border-page-border sticky top-0 bg-container-background z-[1] !pt-3 !rounded-t-xl"
    >
      <div class="flex items-center !gap-1.5">
        <h3
          class="!text-sm 2xl:!text-base !font-normal !text-black-950 !m-0 !max-w-64 2xl:!max-w-[500px] truncate"
        >
          All Custom Dashboards
        </h3>
      </div>
      <div
        class="flex items-center justify-start md:!justify-end !gap-3 flex-wrap md:!flex-nowrap flex-1"
      >
        <el-button
          id="tryUsingAIBtn"
          class="outlined-icon-button flex items-center !gap-1.5 try-using-ai"
          @click="openAiDialog"
        >
          <div class="flex items-center !gap-1.5">
            <icons :iconName="'TryUsingAI'"></icons>
            <div>
              {{ getIsMobile ? 'AI' : 'Try Using AI' }}
            </div>
          </div>
        </el-button>
        <el-input
          prefix-icon="el-icon-search"
          size="mini"
          placeholder="Search"
          v-model="search_string"
          @input="handleSearchInput"
          class="max-w-44 2xl:!max-w-56"
          clearable
        />
        <el-button
          v-if="checkPermissionByPermissionsName('createCustomDashboard')"
          style="background: var(--brand-500) !important"
          class="!py-1.5 !px-4 !rounded-sm hover:!bg-brand-500 !border-none !m-0 !h-auto"
          type="primary"
          @click="openDialog"
        >
          <span class="flex items-center !gap-1.5">
            <img src="@/assets/img/icons/new/add-icon.svg" alt="" class="!size-3" />
            <span class="text-xs font-light text-white">
              {{ getIsMobile ? 'Create' : 'Create Dashboard' }}
            </span>
          </span>
        </el-button>
      </div>
    </div>
    <div v-loading="loading" class="vue-data-table-default !pt-3" element-loading-text="Loading...">
      <data-tables-server
        v-loading="configureLoading"
        :fullscreen="true"
        :data="data"
        :total="total"
        :current-page.sync="currentPage"
        :page-size="pageSize"
        :pagination-props="paginationProps"
        @query-change="loadData"
        style="width: 100%"
        element-loading-text="Fetching dashboards..."
      >
        <el-table-column label="Name" id="name" prop="name" width="250" fixed="left">
          <template slot-scope="scope">
            <span>{{ scope.row.name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Assigned to" id="assigned" prop="assigned" min-width="200">
          <template slot-scope="scope">
            <div style="display: flex" v-if="scope.row.assigned_to && scope.row.assigned_to.length">
              <div
                v-for="(role, i) in getRolesByGroup(scope.row.assigned_to)"
                :key="i + role + 'table'"
                class="mr-1"
              >
                <el-popover trigger="hover">
                  <div style="display: flex; flex-direction: column">
                    <p v-for="user in role.userTypes" :key="'user' + user.id">
                      {{ `${user.name}` }}
                    </p>
                  </div>
                  <el-tag
                    slot="reference"
                    effect="dark"
                    class="!bg-brand-500 !rounded-md !text-xs 2xl:text-smd !border-brand-500 !w-fit !h-fit !py-1"
                  >
                    {{ role.name }}
                  </el-tag>
                </el-popover>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="action" min-width="120" label="Actions">
          <template slot-scope="scope">
            <el-dropdown>
              <span
                class="el-dropdown-link !border !border-brand-500 text-xs text-brand-500 rounded-md px-2 !py-0.5"
              >
                Actions
                <i class="el-icon-caret-bottom el-icon--right"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <a @click="onAssign(scope.row)" style="border: none">
                  <el-dropdown-item icon="el-icon-user" command="edit">
                    Assign to new user
                  </el-dropdown-item>
                </a>
                <a v-if="!getIsMobile" @click="onEdit(scope.row)" style="border: none">
                  <el-dropdown-item icon="el-icon-edit-outline" command="edit">
                    Edit
                  </el-dropdown-item>
                </a>
                <a @click="onDelete(scope.row)" style="border: none">
                  <el-dropdown-item icon="el-icon-delete" command="delete">Delete</el-dropdown-item>
                </a>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
        <el-table-column label="Last modified" width="200">
          <template slot-scope="scope" v-if="scope.row.updated_at">
            <p class="time-stamp mb-0 mt-0">
              {{ scope.row.updated_at | moment('MM-DD-YYYY') }}
            </p>
            <p class="time-stamp mb-0 mt-0">
              {{ scope.row.updated_at | moment('HH:mm:ss') }}
            </p>
          </template>
        </el-table-column>
        <el-table-column label="Created at" width="200">
          <template slot-scope="scope" v-if="scope.row.created_at">
            <p class="time-stamp mb-0 mt-0">
              {{ scope.row.created_at | moment('MM-DD-YYYY') }}
            </p>
            <p class="time-stamp mb-0 mt-0">
              {{ scope.row.created_at | moment('HH:mm:ss') }}
            </p>
          </template>
        </el-table-column>
      </data-tables-server>
    </div>

    <dialog-component
      :title="editingDashboardId ? 'Edit Custom Dashboard' : 'Add Custom Dashboard'"
      :visible="dialogVisible"
      :width="getIsMobile ? '100%' : '30%'"
      @before-close="resetModal"
      :containerMaxWidth="'500px'"
    >
      <div class="m-lr-2 p-tb-2 mb-2">
        <label class="text-lightGray text-xs 2xl:!text-sm !font-normal !mb-1 block">
          Dashboard Name
        </label>
        <el-input
          placeholder="Enter Dashboard Name"
          v-model="dashboard_name"
          class="mb-3"
          style="width: 100%"
        ></el-input>
        <label class="text-lightGray text-xs 2xl:!text-sm !font-normal !mb-1 block !mt-2">
          Select User Type
        </label>
        <el-select
          class="mb-15"
          v-model="dashboard_role"
          filterable
          default-first-option
          placeholder="Select Role"
          multiple
          collapse-tags
          style="width: 100%"
        >
          <el-option
            v-for="item in getUserTypeList"
            :key="item._id"
            :label="getLabel(item)"
            :value="item._id"
          ></el-option>
        </el-select>
        <el-checkbox v-model="main_dashboard_visible">Show In Dashboard</el-checkbox>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          @click="resetModal"
          class="bg-transparent text-black-700 !px-4 !py-1 !rounded-md !text-xs 2xl:!text-sm !border !border-black/10 !mr-1 hover:!text-brand-500 hover:!border-brand-500"
        >
          Cancel
        </el-button>
        <el-button
          style="background: var(--brand-500) !important"
          class="!text-white !px-4 !py-1.5 !rounded-md !text-xs 2xl:!text-sm !border-none"
          type="button"
          @click="saveDashboard"
          :disabled="!dashboard_name"
        >
          Save
        </el-button>
      </span>
    </dialog-component>
    <div
      v-if="this.tryusingAI"
      class="ai-fullscreen-popup"
      v-loading="generatingDashboard"
      :element-loading-text="dashboardLoadingText"
      element-loading-spinner="el-icon-loading"
    >
      <header>
        <el-row
          class="lg:d-flex items-center"
          style="border-bottom: 1px solid #d3d3d3; padding: 10px"
        >
          <el-col :md="3" :lg="5">
            <div class="d-flex items-center">
              <el-button
                type="Secondary"
                @click="goBack"
                class="hover:!text-brand-500 hover:!bg-brand-100 hover:!border-brand-500"
              >
                <i class="el-icon-back">Back</i>
              </el-button>
              <h3 v-if="!dashboardInfo.name" class="mb-0 ml-1">Dashboard</h3>
              <input v-else v-model="dashboardInfo.name" class="dashboard-title-input ml-1" />
            </div>
          </el-col>
          <div class="steps-container">
            <el-col :xs="24" :sm="24" :md="21" :lg="19">
              <el-steps
                :active="active"
                finish-status="success"
                :space="500"
                style="width: 350px; margin-left: 300px"
              >
                <el-step
                  class="step-text"
                  title="Prompt"
                  @click.native="handleActiveChange(0)"
                ></el-step>
                <el-step
                  class="step-text"
                  title="Preview"
                  @click.native="handleActiveChange(1)"
                ></el-step>
              </el-steps>
            </el-col>
          </div>
        </el-row>
      </header>
      <div>
        <section
          v-if="this.active === 0"
          style="text-align: center; min-height: 400px; max-height: 550px; margin-top: 60px"
        >
          <div class="main-content" v-if="creationType == 'Prompt'">
            <el-card class="box-card ai-card" shadow="never">
              <img class="image" src="@/assets/img/Describe.svg" />
              <h4>Describe Your Dashboard</h4>
              <p>Describe your dashboard in a few words or sentence</p>
              <el-input
                type="textarea"
                :rows="4"
                placeholder="Ex:Create dashboard for Property Management System"
                class="input-field"
                v-model="dashboard_creation_prompt"
              ></el-input>
              <div class="generate-button">
                <el-button
                  type="primary"
                  size="medium"
                  style="width: 100%"
                  :disabled="!dashboard_creation_prompt"
                  @click="customDashboardWithAI(0, 'generate')"
                >
                  Generate Dashboard
                </el-button>
              </div>
            </el-card>
          </div>
        </section>
        <section v-if="active === 1">
          <div
            style="height: 75vh; scrollbar-width: thin; overflow-y: auto; padding: 0px 0px 0px 30px"
          >
            <div v-if="activeCustomDashboard">
              <el-row class="dashboard-filters" v-if="checkHasFilter()">
                <el-col :span="24">
                  <CustomDashboardFilters
                    v-if="dateFilter"
                    :getAllEntities="getAllEntities"
                    :getAllCompanyTemplatesData="getAllCompanyTemplatesData"
                    :allDashboardstats="activeCustomDashboard"
                    :integrations="integrations"
                    @updateGlobalDateFilterValue="updateGlobalDateFilterValue"
                    :allIntegrationGlobalFilters="allIntegrationGlobalFilters"
                  ></CustomDashboardFilters>
                </el-col>
              </el-row>
              <el-col :span="24">
                <CustomDashboardStats
                  :getAllEntities="getAllEntities"
                  :getAllCompanyTemplatesData="getAllCompanyTemplatesData"
                  :allDashboardstats="activeCustomDashboard"
                  :hide_options="true"
                  :integrations="integrations"
                  @DateFilterUpdate="DateFilterUpdate"
                  @deleteStat="deleteComponent"
                ></CustomDashboardStats>
              </el-col>
              <div class="display-grid">
                <div
                  v-for="(component, index) in activeCustomDashboard.allTableComponents"
                  :key="component.entity_id + '_' + component.keyValue + '_table_' + index"
                  class="grid-item"
                  :style="
                    expendedIndexs.indexOf(index) != -1 ||
                    getIsMobile ||
                    component.type == 'SCHEDULING'
                      ? 'grid-column: 1/3;'
                      : ''
                  "
                >
                  <CustomDashboardTable
                    v-if="component.type === 'TABLE'"
                    :component="component"
                    :entity_id="component.entity_id"
                    :filter_id="component.filter_id"
                    :index="index"
                    :globalVariablesData="globalVariablesData"
                    :getAllEntities="getAllEntities"
                    :getAllCompanyTemplatesData="getAllCompanyTemplatesData"
                    @expendTable="expendTable"
                    @deleteTable="deleteComponent"
                    :hide_options="true"
                    :allowDelete="true"
                  />
                  <CustomDashboardPieChart
                    v-else-if="component.type === 'CHART'"
                    :component="component"
                    :index="index"
                    @expendTable="expendTable"
                    :hide_options="true"
                    :globalVariablesData="globalVariablesData"
                    :getAllEntities="getAllEntities"
                    :getAllCompanyTemplatesData="getAllCompanyTemplatesData"
                    :integrations="integrations"
                    :isIntegrationEdit="true"
                    :globalDateValue="globalDateValue"
                    :integrationFilters="integration_filters"
                    :customDashboardData="activeCustomDashboard"
                    @deleteChart="deleteComponent"
                  />
                  <CustomDashboardCalendar
                    v-else-if="component.type === 'CALENDAR'"
                    :component="component"
                    :index="index"
                    @expendTable="expendTable"
                    :hide_options="true"
                    :globalVariablesData="globalVariablesData"
                    :getAllEntities="getAllEntities"
                    :getAllCompanyTemplatesData="getAllCompanyTemplatesData"
                    @deleteCalender="deleteComponent"
                  />
                  <CustomDashboardLogins
                    v-else-if="component.type === 'LOGINS'"
                    :component="component"
                    :index="index"
                    @expendTable="expendTable"
                    :hide_options="true"
                    :globalVariablesData="globalVariablesData"
                    :getAllEntities="getAllEntities"
                    :getAllCompanyTemplatesData="getAllCompanyTemplatesData"
                    @deleteLogin="deleteComponent"
                  />
                  <EntityViews
                    v-else-if="component.type === 'ENTITY_VIEW'"
                    :entityViewId="component.entity_view_id"
                    :fromCustomDashboard="true"
                    :index="index"
                    :component="component"
                    :hide_options="true"
                    @expendTable="expendTable"
                    :globalVariablesData="globalVariablesData"
                    :getAllEntities="getAllEntities"
                    :getAllCompanyTemplatesData="getAllCompanyTemplatesData"
                  />
                  <CustomDashboardGaugeChart
                    v-else-if="component.type === 'GAUGE_CHART'"
                    :component="component"
                    :index="index"
                    :hide_options="true"
                    @expendTable="expendTable"
                    @deleteGaugeChart="deleteComponent"
                  />
                  <CustomDashboardNotifications
                    v-else-if="component.type === 'NOTIFICATIONS'"
                    :component="component"
                    :index="index"
                    :hide_options="true"
                    @deleteNotifications="deleteComponent"
                  />
                  <SchedulingEntity
                    v-else-if="component.type === 'SCHEDULING'"
                    :getAllEntities="getAllEntities"
                    :getAllCompanyTemplatesData="getAllCompanyTemplatesData.data || []"
                    :component="component"
                    :index="index"
                    :hide_options="true"
                  />
                </div>
              </div>
            </div>
            <div v-else>
              <p v-if="!this.customDashboardsList">No components found to preview.</p>
            </div>
          </div>
        </section>
        <div style="border-top: 1px solid #d3d3d3; padding: 20px 30px 0px 20px" v-if="active === 1">
          <div class="d-flex">
            <div class="left-section">
              <div class="input-section">
                <span>Request for changes</span>
                <div class="input-send-row">
                  <el-input
                    type="textarea"
                    :rows="3"
                    v-model="changes_prompt"
                    placeholder="Request for changes"
                    class="input-field-icon"
                  />
                  <el-button
                    type="primary"
                    size="small"
                    icon="el-icon-position"
                    :disabled="!changes_prompt || generatingDashboard"
                    @click="customDashboardWithAI(0)"
                  />
                </div>
              </div>
              <div class="or-regen-row">
                <span class="or-text">Or</span>
                <el-button
                  type="primary"
                  size="medium"
                  class="reg-btn"
                  @click="customDashboardWithAI(0, 'regenerate')"
                >
                  Regenerate
                </el-button>
              </div>
            </div>
            <div class="right-section">
              <el-button
                type="primary"
                size="medium"
                class="reg-btn"
                @click="createCustomDashboard"
              >
                Create Dashboard
              </el-button>
            </div>
          </div>
        </div>
        <div
          v-if="unexpectedFormatDialog"
          style="
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background-color: rgba(0, 0, 0, 0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
          "
        >
          <el-card
            shadow="always"
            style="width: 350px; padding: 20px; text-align: center; background-color: #fff"
          >
            <p style="font-size: 16px; color: #606266; margin-bottom: 20px">
              {{ unexpectedFormatMessage || 'Unexpected response received. Please try again.' }}
            </p>
            <el-button type="primary" @click="closeUnexpectedFormatDialog">OK</el-button>
          </el-card>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import { mapGetters } from 'vuex';
  import axios from 'axios';
  import PermissionsHelper from '@/mixins/permissionsHelper';
  import {
    fetchCustomDashboardByPagination,
    deleteCustomDashboard,
    updateAssignedToCustomdashboard,
    addSingleCustomDashboardToLocal,
  } from '@/repo/customDashboardRepo';
  import CustomDashboardStats from '@/components/customDashboard/customDashboardStats.vue';
  import CustomDashboardTable from '@/components/customDashboard/customDashboardTable.vue';
  import CustomDashboardPieChart from '@/components/customDashboard/customDashboardPieChart.vue';
  import CustomDashboardFilters from '@/components/customDashboard/customDashboardFilters.vue';
  import CustomDashboardCalendar from '@/components/customDashboard/customDashboardCalendar.vue';
  import CustomDashboardLogins from '@/components/customDashboard/customDashboardLogins.vue';
  import EntityViews from '@/components/entity/EntityViews.vue';
  import CustomDashboardGaugeChart from '@/components/customDashboard/customDashboardGaugeChart.vue';
  import CustomDashboardNotifications from '@/components/customDashboard/customDashboardNotifications.vue';
  import SchedulingEntity from '@/components/customDashboard/schedulerComponent.vue';
  import returnGlobalIntegrationFilters from '@/mixins/CustomDashboardHelper.js';
  import { postAPICall } from '../../helpers/httpHelper';
  import TemplateBuilderHelper from '@/mixins/templateBuilderHelper.js';
  import moment from 'moment';
  import appConfig from '@/config/app';
  import { fetchGlobalVariables } from '../../repo/globalVariables';
  export default {
    data() {
      return {
        loading: false,
        configureLoading: false,
        dialogVisible: false,
        data: [],
        total: 0,
        currentPage: 1,
        pageSize: 10,
        dashboard_name: '',
        dashboard_role: [],
        main_dashboard_visible: true,
        search_string: '',
        editingDashboardId: '',
        disableEdit: true,
        tryusingAI: false,
        dashboard_creation_prompt: '',
        active: 0,
        creationType: 'Prompt',
        isGenerating: false,
        dashboardInfo: [],
        activeCustomDashboard: null,
        customDashboardsList: [],
        expendedIndexs: [],
        dateFilter: false,
        session_id: '',
        changes_prompt: '',
        generatingDashboard: false,
        getAllCompanyTemplatesData: {},
        getAllEntities: {},
        integration_filters: {},
        integrations: [],
        globalVariablesData: [],
        globalDateValue: {},
        unexpectedFormatDialog: false,
        unexpectedFormatMessage: '',
        dashboardLoadingText: 'Your Dashboard is being generated. Please wait...',
      };
    },
    components: {
      CustomDashboardStats,
      CustomDashboardTable,
      CustomDashboardPieChart,
      CustomDashboardFilters,
      CustomDashboardCalendar,
      CustomDashboardLogins,
      EntityViews,
      CustomDashboardGaugeChart,
      CustomDashboardNotifications,
      SchedulingEntity,
    },
    computed: {
      paginationProps() {
        return {
          pageSizes: [5, 10, 20, 50],
        };
      },
      getAllRolesList() {
        if (this.getAllRoles && this.getAllRoles.data) {
          return this.getAllRoles.data;
        }
        return [];
      },
      getLabel() {
        return (userType) => {
          if (userType && userType.role) {
            return userType.role.title + ' - ' + userType.name;
          } else {
            return userType.name;
          }
        };
      },
      ...mapGetters('roles', ['getAllRoles']),
      ...mapGetters('auth', ['getAuthenticatedUser', 'getActiveWorkspace', 'getUserType']),
      ...mapGetters('company', ['getCompanyDetails']),
      ...mapGetters('customDashboard', [
        'getCreateNewDashboard',
        'getDeleteDashboardStatus',
        'getUpdateDashboardStatus',
        'getCreateNewDashboardError',
        'getUpdateDashboardError',
      ]),
      ...mapGetters('auth', ['getActiveWorkspace', 'getUserTypeList']),
      ...mapGetters('navigationOpen', ['getIsMobile', 'getIsRSOpen']),
      allIntegrationGlobalFilters() {
        return this.returnGlobalIntegrationFilters(this.activeCustomDashboard);
      },
    },
    mixins: [PermissionsHelper, returnGlobalIntegrationFilters, TemplateBuilderHelper],
    async mounted() {
      await Promise.all([
        this.$store.dispatch('auth/getUserTypeList', {
          companyId: this.getActiveWorkspace.company_id,
          isMenu: true,
        }),
        this.getNecessaryInfo(),
      ]);
      this.session_id = `${this.getActiveWorkspace.company_id}#${this.getAuthenticatedUser._id}`;
    },
    methods: {
      closeUnexpectedFormatDialog() {
        this.unexpectedFormatDialog = false;
        if (
          this.getCreateNewDashboardError ||
          this.unexpectedFormatMessage == 'Error while saving dashboard'
        ) {
          this.active = 1;
        } else {
          this.active = 0;
          this.dashboardInfo.name = '';
        }
      },
      updateGlobalDateFilterValue(filter) {
        if (filter && filter.dateValue?.length) {
          this.globalDateValue = {
            startDate: moment(filter.dateValue[0].value[0]).format('YYYY-MM-DD'),
            endDate: moment(filter.dateValue[0].value[1]).format('YYYY-MM-DD'),
          };
        }
      },
      checkHasFilter() {
        return (
          this.activeCustomDashboard.allCustomFilters?.length ||
          (this.activeCustomDashboard.includeGlobalDateFilter &&
            this.activeCustomDashboard.allStatComponents?.length) ||
          this.activeCustomDashboard.allMulCustomFilters?.length ||
          this.activeCustomDashboard.allIntegrationFilters?.length
        );
      },
      DateFilterUpdate() {
        this.dateFilter = true;
      },
      expendTable(index) {
        if (this.expendedIndexs.indexOf(index) == -1) {
          this.activeCustomDashboard.allTableComponents[index]['is_expand'] = true;
          this.expendedIndexs.push(index);
        } else {
          this.expendedIndexs.splice(this.expendedIndexs.indexOf(index), 1);
          this.activeCustomDashboard.allTableComponents[index]['is_expand'] = false;
        }
      },
      async customDashboardWithAI(index, actionType = 'generate') {
        this.activeCustomDashboard = null;
        this.generatingDashboard = true;
        if (actionType === 'generate') {
          this.dashboardLoadingText = 'Your Dashboard is being generated. Please wait...';
        } else if (actionType === 'regenerate') {
          this.dashboardLoadingText = 'Your Dashboard is being regenerating. Please wait...';
        }
        let queryText = '';
        if (actionType === 'regenerate') {
          queryText = this.dashboard_creation_prompt?.trim();
        } else {
          queryText = this.changes_prompt?.trim()
            ? this.changes_prompt.trim()
            : this.dashboard_creation_prompt?.trim();
        }

        if (!queryText) {
          this.$message.warning('Please enter a prompt to continue.');
          this.generatingDashboard = false;
          return;
        }
        const payload = {
          company_id: this.getActiveWorkspace.company_id,
          query: queryText,
          session_id: this.session_id,
          flag: false,
        };
        try {
          const response = await axios.post(
            appConfig.AI_BASE_KODEFAST_URL + '/dashboard/with_ai',
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
          const responseData = response.data;
          if (responseData.components && Array.isArray(responseData.components.components)) {
            this.dashboardInfo = {
              name: responseData.components.name
                ? responseData.components.name.replace(/(\s*Dashboard\s*)$/i, '')
                : 'Dashboard',
              include_global_date_filter:
                responseData.components.include_global_date_filter || false,
              Custom_date_value: responseData.components.Custom_date_value || [],
              date_value: responseData.components.date_value || '',
              components: responseData.components.components,
              allow_user_change_order: responseData.components.allow_user_change_order || false,
            };
          } else if (responseData.components && responseData.components.unique_key) {
            const modifiedComponent = responseData.components;
            const currentComponents = this.dashboardInfo.components || [];
            const updatedComponents = currentComponents.map((comp) => {
              if (comp.unique_key === modifiedComponent.unique_key) {
                return { ...comp, ...modifiedComponent };
              }
              return comp;
            });
            this.dashboardInfo.components = updatedComponents;
          } else {
            this.unexpectedFormatMessage = 'Unexpected response received. Please try again.';
            this.unexpectedFormatDialog = true;
            this.generatingDashboard = false;
            return;
          }
          this.fetchTemplatesAndEntities();
        } catch (error) {
          this.generatingDashboard = false;
          console.error('Failed to fetch dashboard:', error);
          return;
        }
        this.customDashboardsList = [this.dashboardInfo];
        if (!this.customDashboardsList || !this.customDashboardsList[index]) {
          return;
        }
        let allMainComponents = this.customDashboardsList[index].components.filter((e) =>
          [
            'TABLE',
            'CHART',
            'CALENDAR',
            'LOGINS',
            'ENTITY_VIEW',
            'GAUGE_CHART',
            'NOTIFICATIONS',
            'SCHEDULING',
          ].includes(e.type)
        );
        allMainComponents = allMainComponents.filter((e) => {
          if (e.type === 'CHART') {
            return (
              e.pie_chart_field && e.pie_chart_field !== 'none' && e.pie_chart_field.includes('#')
            );
          }
          return true;
        });
        allMainComponents = allMainComponents.filter((e) => {
          const needsEntityId = ['TABLE', 'CHART', 'STAT', 'ENTITY_VIEW'];
          if (needsEntityId.includes(e.type)) {
            return e.entity_id && typeof e.entity_id === 'string' && e.entity_id.trim().length > 0;
          }
          return true;
        });
        this.activeCustomDashboard = {
          name: this.dashboardInfo.name,
          includeGlobalDateFilter: this.dashboardInfo.include_global_date_filter || false,
          date_value: this.dashboardInfo.date_value || '',
          Custom_date_value: this.dashboardInfo.Custom_date_value || [],
          allTableComponents: allMainComponents.sort((a, b) => (a.order < b.order ? -1 : 1)),
          allStatComponents: this.customDashboardsList[index].components
            .filter((e) => e.type === 'STAT')
            .map((e) => {
              if (e?.data_source === 'INTEGRATIONS') {
                e.dates = { startDate: '', endDate: '' };
              }
              return e;
            }),
          allCustomFilters: this.customDashboardsList[index].components
            .filter((e) => e.type === 'CUSTOM_FILTER')
            .map((e) => {
              e.value = '';
              e.selected_filter = '';
              return e;
            }),
          allMulCustomFilters: this.customDashboardsList[index].components
            .filter((e) => e.type === 'MULTIPLE_CUSTOM_FILTER')
            .map((e) => {
              e.value = '';
              e.selected_filter = '';
              return e;
            }),
          keyValue: (Math.random() + 1).toString(36).substring(7),
          allowUserChangeOrder: this.customDashboardsList[index].allow_user_change_order || false,
        };

        const allEntities = [
          ...this.activeCustomDashboard.allTableComponents.flatMap((e) => e.entity_id),
          ...this.activeCustomDashboard.allStatComponents.flatMap((e) => e.entity_id),
          ...this.activeCustomDashboard.allCustomFilters.flatMap((e) => e.entity_id),
        ];
        this.activeCustomDashboard.allCustomFilters =
          this.activeCustomDashboard.allCustomFilters.filter(
            (e) => allEntities.indexOf(e.entity_id) !== -1
          );
        this.activeCustomDashboard.allTableComponents =
          this.activeCustomDashboard.allTableComponents.map((e) => {
            e.keyValue = (Math.random() + 1).toString(36).substring(7);
            return e;
          });

        this.activeCustomDashboard.allCustomFilters =
          this.activeCustomDashboard.allCustomFilters.map((e) => {
            e.keyValue = (Math.random() + 1).toString(36).substring(7);
            return e;
          });
      },
      async createCustomDashboard() {
        try {
          this.generatingDashboard = true;
          this.dashboardLoadingText = 'Your Dashboard is being created. Please wait...';
          const createPayload = {
            name: this.dashboardInfo.name || 'AI Dashboard 2',
            assigned_to: this.getUserType._id || [],
            main_dashboard_visible: true,
          };

          await this.$store.dispatch('customDashboard/fetchCreateNewDashboard', createPayload);
          const createdDashboard = this.getCreateNewDashboard;

          if (!createdDashboard || !createdDashboard._id) {
            this.unexpectedFormatMessage =
              this.getCreateNewDashboardError?.message || 'Error while saving dashboard';
            this.unexpectedFormatDialog = true;
            return;
          }
          let cleanedComponents = [];
          if (this.dashboardInfo.components && Array.isArray(this.dashboardInfo.components)) {
            cleanedComponents = this.dashboardInfo.components.map((comp) => {
              const copy = { ...comp };
              delete copy.unique_key;
              return copy;
            });
          }

          await addSingleCustomDashboardToLocal(createdDashboard);

          const updatePayload = {
            name: this.dashboardInfo.name,
            components: cleanedComponents || [],
            include_global_date_filter: this.dashboardInfo.include_global_date_filter || false,
            Custom_date_value: this.dashboardInfo.Custom_date_value || [],
            allow_user_change_order: this.dashboardInfo.allow_user_change_order || false,
          };

          await this.$store.dispatch('customDashboard/updateDashboard', {
            id: createdDashboard._id,
            params: updatePayload,
          });

          if (this.getUpdateDashboardStatus) {
            await updateAssignedToCustomdashboard(createdDashboard._id, updatePayload);
            this.$notify({
              title: 'Success',
              message: 'Dashboard created successfully',
              type: 'success',
            });
          } else {
            this.unexpectedFormatMessage =
              this.getUpdateDashboardError?.message || 'Error while updating dashboard';
            this.unexpectedFormatDialog = true;
            return;
          }

          this.$router.push({
            name: 'custom-dashboard-config',
            params: { dashboardId: createdDashboard._id },
          });
        } catch (error) {
          this.unexpectedFormatMessage =
            error?.response?.data?.message || error?.message || 'Failed to generate dashboard.';
          this.unexpectedFormatDialog = true;
        } finally {
          this.generatingDashboard = false;
          this.dashboardLoadingText = '';
        }
      },
      async fetchTemplatesAndEntities() {
        let [integrations, allTemplates, getAllEntities, getAllGlobalVariables] = await Promise.all(
          [
            postAPICall('GET', `integrations`, {
              get_all: true,
            }),
            this.fetchAllTemplates(),
            this.fetchAllEntities(),
            fetchGlobalVariables({ get_all: true }),
          ]
        );
        this.getAllCompanyTemplatesData = { data: allTemplates };
        this.getAllEntities = { data: getAllEntities };
        if (integrations?.data) {
          this.integrations = integrations.data;
        }
        this.globalVariablesData = getAllGlobalVariables?.data || [];
        this.changes_prompt = '';
        this.generatingDashboard = false;
        this.active = 1;
      },
      async goBack() {
        if (this.active === 1) {
          this.active = 0;
        } else {
          this.tryusingAI = false;
        }
      },
      handleActiveChange(target) {
        if (target < this.active) {
          switch (target) {
            case 0:
              break;
            case 1:
              break;
          }
          this.active = target;
        }
      },
      openAiDialog() {
        this.tryusingAI = true;
        this.dashboard_creation_prompt =
          'Create Dashboard for ' + `${this.getCompanyDetails.legal_name}`;
      },
      async handleSearchInput() {
        this.currentPage = 1;
        await this.getNecessaryInfo();
      },
      shouldDisableEdit() {
        return this.getIsMobile;
      },
      async onDelete(data) {
        if (
          data &&
          data.created_by &&
          this.getAuthenticatedUser &&
          this.getAuthenticatedUser._id &&
          this.getAuthenticatedUser._id != data.created_by &&
          !this.checkPermissionByPermissionsName('deleteCustomDashboard')
        ) {
          this.$notify.error({
            title: 'Error',
            message: 'Permission denied for delete dashboard.Please contact owner.',
          });
        } else {
          this.$confirm('Are you sure to delete?', 'Warning', {
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            type: 'warning',
          }).then(() => {
            this.deleteDashboard(data);
          });
        }
      },
      async deleteDashboard(data) {
        this.loading = true;
        await this.$store.dispatch('customDashboard/deleteDashboard', data._id);
        if (this.getDeleteDashboardStatus) {
          await deleteCustomDashboard(data._id);
          this.loading = false;
          this.$notify({
            title: 'Success',
            message: 'Deleted successfully',
            type: 'success',
          });
          await this.getNecessaryInfo();
        } else {
          this.loading = false;
          this.$notify({
            title: 'Error',
            message: 'Error while deleting dashboard',
            type: 'error',
          });
        }
      },
      resetModal() {
        this.dashboard_name = '';
        this.dashboard_role = '';
        this.main_dashboard_visible = true;
        this.editingDashboardId = '';
        this.dialogVisible = false;
      },
      onAssign(data) {
        if (
          data &&
          data.created_by &&
          this.getAuthenticatedUser &&
          this.getAuthenticatedUser._id &&
          this.getAuthenticatedUser._id != data.created_by &&
          !this.checkPermissionByPermissionsName('assignToNewUserCustomDashboard')
        ) {
          this.$notify.error({
            title: 'Error',
            message: 'Permission denied for assign dashboard to new user.Please contact owner.',
          });
        } else {
          this.dashboard_name = data.name;
          this.dashboard_role = data.assigned_to.filter((e) => e);
          this.main_dashboard_visible = data.main_dashboard_visible;
          this.editingDashboardId = data._id;
          this.dialogVisible = true;
        }
      },
      onEdit(data) {
        if (
          data &&
          data.created_by &&
          this.getAuthenticatedUser &&
          this.getAuthenticatedUser._id &&
          this.getAuthenticatedUser._id != data.created_by &&
          !this.checkPermissionByPermissionsName('editCustomDashboard')
        ) {
          this.$notify.error({
            title: 'Error',
            message: 'Permission denied for edit dashboard.Please contact owner.',
          });
        } else {
          this.$router.push({
            name: 'custom-dashboard-config',
            params: {
              dashboardId: data._id,
            },
          });
        }
      },
      capitalizeFirstLetter(string) {
        string = string.toLowerCase();
        return string.charAt(0).toUpperCase() + string.slice(1);
      },
      getRolesByGroup(data) {
        let result = [];
        (this.getUserTypeList || []).forEach((type) => {
          if (data.indexOf(type._id) !== -1 && type.role && type.role._id) {
            let existedRole = result.find((e) => e.roleId == type.role._id);
            if (existedRole) {
              let exitedIndex = result.findIndex((e) => e.roleId == type.role._id);
              existedRole.userTypes.push({
                id: type._id,
                name: this.capitalizeFirstLetter(type.name),
              });
              result[exitedIndex] = { ...existedRole };
            } else {
              result.push({
                name: type.role.title,
                roleId: type.role._id,
                userTypes: [
                  {
                    id: type._id,
                    name: this.capitalizeFirstLetter(type.name),
                  },
                ],
              });
            }
          }
        });
        return result;
      },
      async saveDashboard() {
        this.dashboard_name = this.dashboard_name.trim();
        if (!this.dashboard_name) {
          this.$message.error('Please enter dashboard name');
          return;
        }
        let params = {
          name: this.dashboard_name,
          assigned_to: this.dashboard_role || [],
          main_dashboard_visible: this.main_dashboard_visible,
        };
        if (this.editingDashboardId) {
          await this.$store.dispatch('customDashboard/updateDashboard', {
            id: this.editingDashboardId,
            params: params,
          });
          if (this.getUpdateDashboardStatus) {
            await updateAssignedToCustomdashboard(this.editingDashboardId, params);
            this.$notify({
              title: 'Success',
              message: 'Updated successfully',
              type: 'success',
            });
            this.resetModal();
            this.getNecessaryInfo();
          } else {
            let msg =
              this.getUpdateDashboardError && this.getUpdateDashboardError.message
                ? this.getUpdateDashboardError.message
                : 'Error while editing dashboard';
            this.$notify({
              title: 'Error',
              message: msg,
              type: 'error',
            });
          }
        } else {
          await this.$store.dispatch('customDashboard/fetchCreateNewDashboard', params);
          if (this.getCreateNewDashboard && this.getCreateNewDashboard._id) {
            await addSingleCustomDashboardToLocal(this.getCreateNewDashboard);
            this.$router.push({
              name: 'custom-dashboard-config',
              params: {
                dashboardId: this.getCreateNewDashboard._id,
              },
            });
          } else {
            let msg =
              this.getCreateNewDashboardError && this.getCreateNewDashboardError.message
                ? this.getCreateNewDashboardError.message
                : 'Error while saving data';
            this.$notify({
              title: 'Error',
              message: msg,
              type: 'error',
            });
          }
        }
      },
      async getNecessaryInfo() {
        this.configureLoading = true;
        let params = {
          page: this.currentPage,
          limit: this.pageSize > 0 ? this.pageSize : 10,
        };
        if (this.search_string) {
          params.search_string = this.search_string;
        }
        const [getAllDashboards] = await Promise.all([
          fetchCustomDashboardByPagination(params),
          this.$store.dispatch('auth/getUserTypeList', {
            companyId: this.getActiveWorkspace.company_id,
            isMenu: true,
          }),
        ]);
        if (getAllDashboards) {
          this.data = getAllDashboards.data;
          this.total = getAllDashboards.total;
        }
        this.configureLoading = false;
      },
      async loadData(data) {
        if (data && data.type != 'init') {
          this.pageSize = data.pageSize;
          this.currentPage = data.page;
          await this.getNecessaryInfo();
        }
      },
      async openDialog() {
        this.resetModal();
        this.dialogVisible = true;
      },
    },
  };
</script>
<style lang="scss" scoped>
  .dashboard-title-input {
    font-size: 1.2rem;
    color: #677788;
    background: transparent;
    border: 1px solid #dcdfe6;
    width: 100%;
  }

  .dashboard-title-input:focus {
    border-bottom: 2px solid #409eff;
  }

  .left-section {
    display: flex;
    flex-direction: row;
    gap: 12px;
    flex: 1;
    min-width: 300px;
  }

  .input-send-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .or-regen-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
  }

  .right-section {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    min-width: 200px;
    margin-top: 20px;
  }

  .input-section {
    display: flex;
    flex-direction: column;
  }

  .input-field-icon {
    margin-right: 8px;
    min-width: 400px;
  }

  .el-icon {
    font-size: 50px;
    color: #409eff;
  }

  @media (max-width: 480px) {
    .steps-container {
      margin-left: -277px;
    }

    .box-card p {
      margin-left: 55px;
    }
  }

  .display-grid {
    display: grid !important;
    grid-template-columns: 49% 49% !important;
    column-gap: 10px !important;
    row-gap: 1em !important;
    padding: 25px 10px 25px 0px;
  }

  .ai-card {
    width: 40%;
    padding: 10px;

    .image {
      width: 50px;
      margin-bottom: 20px;
    }

    h4 {
      font-size: 15px;
      margin-bottom: 10px;
    }

    p {
      text-align: left;
    }
  }

  .generate-button {
    margin-top: 20px;
    width: 100%;
  }

  .main-content {
    margin-top: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ai-fullscreen-popup {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: white;
    z-index: 9999;
    overflow-y: auto;
  }

  .try-using-ai {
    padding: 4px 14px;
    border-radius: 35px;
    transition: all 0.3s ease;
    background-color: transparent !important;
    border-color: var(--primary-color-brand) !important;
    color: var(--primary-color-brand) !important;
  }

  .try-using-ai:hover {
    color: #fff;
    background-color: var(--primary-color-brand) !important;
  }

  .try-using-ai img {
    margin-right: 10px;
  }
</style>
